import download from '@/helpers/download'
import { RootState } from '@/stores'
import { context } from '@react-three/fiber'
import { useContext } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { ToastHelper } from '@cfs-fixings/shared'
import { GLTFExporter } from 'three/examples/jsm/exporters/GLTFExporter.js'
import { OBJExporter } from 'three/examples/jsm/exporters/OBJExporter.js'
import { STLExporter } from 'three/examples/jsm/exporters/STLExporter.js'
import { deselectComponent } from '@/stores/active-design'
import { PropertyTabs, setCurrentPropertiesTab } from '@/stores/selected-properties-tab'

type DownloadFormat = 'gltf' | 'obj' | 'stl'

class DownloadModelEvent extends Event {
  constructor (format: DownloadFormat) {
    super(DownloadModelEvent.eventName)
    this.format = format
  }

  public format: DownloadFormat

  public static eventName = 'download-model'
}

const useDownloadModel = () => {
  // Using context to get the scene since this might be used outside of the Canvas
  const threeContext = useContext(context)

  const dispatch = useDispatch()

  const designName = useSelector((state: RootState) => state.activeDesign.design?.name)

  const handleDownloadError = () => {
    ToastHelper.error('Failed to download model', 'Please try again later')
  }

  const handleDownload = async (event: Event) => {
    // Deselect any component before exporting
    dispatch(deselectComponent())
    dispatch(setCurrentPropertiesTab(PropertyTabs.Results))

    // Wait for the scene to update on the next render
    await new Promise(resolve => setTimeout(resolve))

    const scene = threeContext.getState().scene.clone()

    const objectIdsToRemove: string[] = []

    scene.traverse(object => {
      if (object.type.endsWith('Camera') || object.type.endsWith('Light')) {
        objectIdsToRemove.push(object.uuid)
      }
    })

    objectIdsToRemove.forEach(id => {
      const object = scene.getObjectByProperty('uuid', id)
      if (object) {
        object.parent?.remove(object)
      }
    })

    const exportFunc = {
      gltf: () => {
        const exporter = new GLTFExporter()

        exporter.parse(
          scene,
          (gltf) => {
            download(new Blob([JSON.stringify(gltf)], { type: 'application/json' }), `${designName}.gltf`)
          },
          () => handleDownloadError(),
          {
            embedImages: false,
            maxTextureSize: 512,
          },
        )
      },

      obj: () => {
        const exporter = new OBJExporter()

        try {
          const data = exporter.parse(scene)
          download(new Blob([data]), `${designName}.obj`)
        } catch (error) {
          handleDownloadError()
        }
      },

      stl: () => {
        const exporter = new STLExporter()

        try {
          const data = exporter.parse(scene)
          download(new Blob([data]), `${designName}.stl`)
        } catch (error) {
          handleDownloadError()
        }
      },
    }[(event as DownloadModelEvent).format]

    exportFunc()
  }

  const registerDownloadModelListener = () => {
    document.addEventListener(DownloadModelEvent.eventName, handleDownload)
  }

  const unregisterDownloadModelListener = () => {
    document.removeEventListener(DownloadModelEvent.eventName, handleDownload)
  }

  const downloadModel = (format: DownloadFormat) => {
    document.dispatchEvent(new DownloadModelEvent(format))
  }

  return {
    registerDownloadModelListener,
    unregisterDownloadModelListener,
    downloadModel,
  }
}

export default useDownloadModel
